import apiClient from "utils/apiClient";
import { ACTOR_ID_HEADER_KEY, OLREADIA_SERVICE_BASE_URL, PROFILE_SERVICE_BASE_URL } from "constants";

const API_V1 = `${OLREADIA_SERVICE_BASE_URL}/api/v1`;
const PROFILE_API_V1 = `${PROFILE_SERVICE_BASE_URL}/api/v1`;

const unwrap = (response) => response.data;

const withPagination = (params = {}) => {
  const { page = 0, size = 20, ...rest } = params;
  return { page, size, ...rest };
};

const withActor = (actorId) =>
  actorId ? { headers: { [ACTOR_ID_HEADER_KEY]: actorId } } : {};

const normalizeOrganizationPayload = (data) => {
  if (!data || typeof data !== "object") return data;

  const payload = { ...data };
  delete payload.businessLocationType;
  delete payload.businessLocationTypeId;
  delete payload.businessLocationTypeCode;

  const organizationLocationTypeId =
    data.organizationLocationTypeId ||
    data.organizationLocationType?.id ||
    data.businessLocationTypeId ||
    data.businessLocationType?.id ||
    data.businessLocationType ||
    null;

  return {
    ...payload,
    ...(organizationLocationTypeId ? { organizationLocationTypeId } : {}),
  };
};

const normalizeBusinessLocationPayload = (data) => {
  if (!data || typeof data !== "object") return data;

  const payload = { ...data };
  delete payload.businessLocationType;
  delete payload.businessLocationTypeId;
  delete payload.businessLocationTypeCode;

  const organizationLocationTypeId =
    data.organizationLocationTypeId ||
    data.organizationLocationType?.id ||
    data.businessLocationTypeId ||
    data.businessLocationType?.id ||
    data.businessLocationType ||
    null;

  return {
    ...payload,
    ...(organizationLocationTypeId ? { organizationLocationTypeId } : {}),
  };
};

const normalizeOrganizationResponse = (organization) => {
  if (!organization || typeof organization !== "object") return organization;

  return {
    ...organization,
    businessLocationType:
      organization.businessLocationType || organization.organizationLocationType,
    businessLocations:
      organization.businessLocations || organization.organizationLocations || [],
    users: organization.users || organization.organizationUsers || [],
  };
};

const normalizeOrganizationPage = (page) => {
  if (Array.isArray(page)) return page.map(normalizeOrganizationResponse);
  if (!page || typeof page !== "object") return page;
  return {
    ...page,
    content: Array.isArray(page.content)
      ? page.content.map(normalizeOrganizationResponse)
      : page.content,
  };
};

const normalizeOrganizationLocationResponse = (location) => {
  if (!location || typeof location !== "object") return location;

  return {
    ...location,
    businessLocationType:
      location.businessLocationType || location.organizationLocationType,
    organizationId: location.organizationId || location.organizationId,
  };
};

const normalizeOrganizationLocationPage = (page) => {
  if (Array.isArray(page)) return page.map(normalizeOrganizationLocationResponse);
  if (!page || typeof page !== "object") return page;
  return {
    ...page,
    content: Array.isArray(page.content)
      ? page.content.map(normalizeOrganizationLocationResponse)
      : page.content,
  };
};

export const olreadiaService = {
  backgroundChecks: {
    listForProfessionProfile: async (professionProfileId, checkType) =>
      apiClient
        .get(`${API_V1}/background-checks/profession-profiles/${professionProfileId}`, {
          params: checkType ? { checkType } : undefined,
        })
        .then(unwrap),

    get: async (backgroundCheckId) =>
      apiClient.get(`${API_V1}/background-checks/${backgroundCheckId}`).then(unwrap),

    requestForProfessionProfile: async (professionProfileId, data, actorId) =>
      apiClient
        .post(`${API_V1}/background-checks/profession-profiles/${professionProfileId}/request`, data, withActor(actorId))
        .then(unwrap),

    initiateForProfessionProfile: async (professionProfileId, data, actorId) =>
      apiClient
        .post(`${API_V1}/background-checks/profession-profiles/${professionProfileId}/initiate`, data, withActor(actorId))
        .then(unwrap),

    createForProfessionProfile: async (professionProfileId, data, actorId) =>
      apiClient
        .post(`${API_V1}/background-checks/profession-profiles/${professionProfileId}`, data, withActor(actorId))
        .then(unwrap),

    update: async (backgroundCheckId, data, actorId) =>
      apiClient
        .put(`${API_V1}/background-checks/${backgroundCheckId}`, data, withActor(actorId))
        .then(unwrap),

    initiate: async (backgroundCheckId, actorId) =>
      apiClient
        .post(`${API_V1}/background-checks/${backgroundCheckId}/initiate`, null, withActor(actorId))
        .then(unwrap),

    cancel: async (backgroundCheckId, actorId) =>
      apiClient
        .put(`${API_V1}/background-checks/${backgroundCheckId}/cancel`, null, withActor(actorId))
        .then(unwrap),

    submitItemResult: async (backgroundCheckId, data, actorId) =>
      apiClient
        .patch(`${API_V1}/background-checks/${backgroundCheckId}/check`, data, withActor(actorId))
        .then(unwrap),
  },

  helperProfiles: {
    list: async (params = {}) =>
      apiClient
        .get(`${API_V1}/helper-profiles`, { params })
        .then(unwrap),

    get: async (userId) =>
      apiClient.get(`${API_V1}/helper-profiles/${userId}`).then(unwrap),

    updateStatus: async (userId, data) =>
      apiClient.put(`${API_V1}/helper-profiles/${userId}/status`, data).then(unwrap),
  },

  professionProfiles: {
    list: async (params = {}) =>
      apiClient
        .get(`${API_V1}/profession-profiles`, { params: withPagination(params) })
        .then(unwrap),

    get: async (professionProfileId) =>
      apiClient.get(`${API_V1}/profession-profiles/${professionProfileId}`).then(unwrap),

    listByUser: async (userId, params = {}) =>
      apiClient
        .get(`${API_V1}/profession-profiles`, {
          params: { ...withPagination(params), userId: userId || undefined },
        })
        .then(unwrap),

    listForUser: async (userId, params = {}) =>
      olreadiaService.professionProfiles.listByUser(userId, params),

    listRequirements: async (params = {}) =>
      apiClient
        .get(`${API_V1}/profession-profiles/requirements`, { params })
        .then(unwrap),

    createForUser: async (userId, data) =>
      apiClient
        .post(`${API_V1}/profession-profiles`, data, { params: { userId: userId || undefined } })
        .then(unwrap),

    updateForUser: async (userId, data) =>
      apiClient
        .put(`${API_V1}/profession-profiles`, data, { params: { userId: userId || undefined } })
        .then(unwrap),

    delete: async (professionProfileId, params = {}) =>
      apiClient
        .delete(`${API_V1}/profession-profiles/${professionProfileId}`, { params })
        .then(unwrap),
  },

  organizations: {
    list: async (params = {}) =>
      apiClient
        .get(`${PROFILE_API_V1}/organizations`, { params: withPagination(params) })
        .then(unwrap)
        .then(normalizeOrganizationPage),

    listForUser: async (actorId, params = {}) =>
      apiClient
        .get(`${PROFILE_API_V1}/organizations/user`, {
          params: withPagination(params),
          ...withActor(actorId),
        })
        .then(unwrap)
        .then(normalizeOrganizationPage),

    get: async (organizationId) =>
      apiClient
        .get(`${PROFILE_API_V1}/organizations/${organizationId}`)
        .then(unwrap)
        .then(normalizeOrganizationResponse),

    create: async (data, actorId) =>
      apiClient
        .post(
          `${PROFILE_API_V1}/organizations`,
          normalizeOrganizationPayload(data),
          withActor(actorId),
        )
        .then(unwrap)
        .then(normalizeOrganizationResponse),

    update: async (organizationId, data, actorId) =>
      apiClient
        .patch(
          `${PROFILE_API_V1}/organizations/${organizationId}`,
          normalizeOrganizationPayload(data),
          withActor(actorId),
        )
        .then(unwrap)
        .then(normalizeOrganizationResponse),

    delete: async (organizationId, actorId) =>
      apiClient
        .delete(`${PROFILE_API_V1}/organizations/${organizationId}`, withActor(actorId))
        .then(unwrap),

    users: {
      list: async (organizationId, params = {}) =>
        apiClient
          .get(`${PROFILE_API_V1}/organizations/${organizationId}/users`, {
            params: withPagination(params),
          })
          .then(unwrap),

      add: async (organizationId, data, actorId) =>
        apiClient
          .post(`${PROFILE_API_V1}/organizations/${organizationId}/users`, data, withActor(actorId))
          .then(unwrap),

      update: async (organizationId, userId, data, actorId) =>
        apiClient
          .patch(
            `${PROFILE_API_V1}/organizations/${organizationId}/users/${userId}`,
            data,
            withActor(actorId),
          )
          .then(unwrap),

      remove: async (organizationId, userId, actorId) =>
        apiClient
          .delete(
            `${PROFILE_API_V1}/organizations/${organizationId}/users/${userId}`,
            withActor(actorId),
          )
          .then(unwrap),
    },

    locations: {
      list: async (organizationId, params = {}) =>
        apiClient
          .get(`${PROFILE_API_V1}/organizations/${organizationId}/organization-locations`, {
            params: withPagination(params),
          })
          .then(unwrap)
          .then(normalizeOrganizationLocationPage),

      get: async (organizationId, businessLocationId) =>
        apiClient
          .get(
            `${PROFILE_API_V1}/organizations/${organizationId}/organization-locations/${businessLocationId}`,
          )
          .then(unwrap)
          .then(normalizeOrganizationLocationResponse),

      create: async (organizationId, data, actorId) =>
        apiClient
          .post(
            `${PROFILE_API_V1}/organizations/${organizationId}/organization-locations`,
            normalizeBusinessLocationPayload(data),
            withActor(actorId),
          )
          .then(unwrap)
          .then(normalizeOrganizationLocationResponse),

      update: async (organizationId, businessLocationId, data, actorId) =>
        apiClient
          .patch(
            `${PROFILE_API_V1}/organizations/${organizationId}/organization-locations/${businessLocationId}`,
            normalizeBusinessLocationPayload(data),
            withActor(actorId),
          )
          .then(unwrap)
          .then(normalizeOrganizationLocationResponse),

      delete: async (organizationId, businessLocationId, actorId) =>
        apiClient
          .delete(
            `${PROFILE_API_V1}/organizations/${organizationId}/organization-locations/${businessLocationId}`,
            withActor(actorId),
          )
          .then(unwrap),
    },
  },

  businessLocationTypes: {
    list: async (params = {}) =>
      apiClient
        .get(`${PROFILE_API_V1}/organization-location-types`, {
          params: withPagination({ size: 50, ...params }),
        })
        .then(unwrap),
  },

  serviceOfferings: {
    list: async (params = {}) =>
      apiClient
        .get(`${PROFILE_API_V1}/service-offerings`, { params: withPagination(params) })
        .then(unwrap),

    get: async (serviceId) =>
      apiClient.get(`${PROFILE_API_V1}/service-offerings/${serviceId}`).then(unwrap),

    create: async (data, actorId) =>
      apiClient.post(`${PROFILE_API_V1}/service-offerings`, data, withActor(actorId)).then(unwrap),

    update: async (serviceId, data, actorId) =>
      apiClient
        .patch(`${PROFILE_API_V1}/service-offerings/${serviceId}`, data, withActor(actorId))
        .then(unwrap),

    delete: async (serviceId, actorId) =>
      apiClient.delete(`${PROFILE_API_V1}/service-offerings/${serviceId}`, withActor(actorId)).then(unwrap),

    organizationServices: {
      list: async (organizationId) =>
        apiClient.get(`${PROFILE_API_V1}/service-offerings/organizations/${organizationId}`).then(unwrap),

      add: async (organizationId, serviceId, actorId) =>
        apiClient
          .post(
            `${PROFILE_API_V1}/service-offerings/organizations/${organizationId}/organization-offerings/${serviceId}`,
            null,
            withActor(actorId),
          )
          .then(unwrap),

      remove: async (organizationId, serviceId, actorId) =>
        apiClient
          .delete(
            `${PROFILE_API_V1}/service-offerings/organizations/${organizationId}/organization-offerings/${serviceId}`,
            withActor(actorId),
          )
          .then(unwrap),
    },

    businessLocationServices: {
      list: async (organizationId, businessLocationId) =>
        apiClient
          .get(
            `${PROFILE_API_V1}/service-offerings/organizations/${organizationId}/organization-locations/${businessLocationId}`,
          )
          .then(unwrap),

      add: async (organizationId, businessLocationId, serviceId, actorId) =>
        apiClient
          .post(
            `${PROFILE_API_V1}/service-offerings/organizations/${organizationId}/organization-locations/${businessLocationId}/organization-offerings/${serviceId}`,
            null,
            withActor(actorId),
          )
          .then(unwrap),

      remove: async (organizationId, businessLocationId, serviceId, actorId) =>
        apiClient
          .delete(
            `${PROFILE_API_V1}/service-offerings/organizations/${organizationId}/organization-locations/${businessLocationId}/organization-offerings/${serviceId}`,
            withActor(actorId),
          )
          .then(unwrap),
    },
  },
};

export default olreadiaService;
